import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaUniversity,
  FaGraduationCap,
  FaCalendarAlt,
  FaInfoCircle,
  FaGlobe,
  FaMapMarkedAlt,
  FaChevronRight,
  FaLock,
} from "react-icons/fa";

const fieldVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const FormField = ({
  type = "text",
  label,
  name,
  value,
  onChange,
  placeholder,
  options = [],
  readOnly = false,
  icon = null,
  disabled = false,
}) => (
  <motion.div variants={fieldVariants} className="mb-4">
    <label
      htmlFor={name}
      className="block text-xs font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">
          {icon}
        </span>
      )}
      {type === "select" ? (
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required
          disabled={disabled}
          className={`w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm pl-${
            icon ? "9" : "3"
          } shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition ${
            disabled ? "bg-gray-50 cursor-not-allowed" : "hover:border-gray-300"
          }`}
        >
          <option value="">{`Select ${label}`}</option>
          {options.map((opt) =>
            typeof opt === "object" ? (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ) : (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )
          )}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          disabled={disabled}
          required={!readOnly && !disabled}
          className={`w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm pl-${
            icon ? "9" : "3"
          } shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition ${
            readOnly || disabled
              ? "bg-gray-50 cursor-not-allowed"
              : "hover:border-gray-300"
          }`}
        />
      )}
    </div>
  </motion.div>
);

const SectionHeader = ({ icon, title, description }) => (
  <div className="flex items-start mb-4">
    <div className="bg-blue-100 p-1.5 rounded-md mr-3 mt-0.5">
      {React.cloneElement(icon, { className: "text-blue-700 text-xs" })}
    </div>
    <div>
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {description && (
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      )}
    </div>
  </div>
);

export default function RegisterPage1({ onNext }) {
  const LOCAL_KEY = "register_step1";
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(LOCAL_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          group: "",
          groupId: "",
          residency: "",
          city: "",
          country: "",
          university: "",
          programme: "",
          intake: "",
          deliveryPattern: "",
          entryLevel: "",
          courseFee: "",
          firstYearFee: "",
          loanFunded: "",
          apl: false,
          heardFrom: "",
          euSettlementCode: "",
          programmeGroup: "",
          programmeField: "",
        };
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isLoadingIntakes, setIsLoadingIntakes] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [residencyOptions, setResidencyOptions] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [deliveryPatterns, setDeliveryPatterns] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [programmeFields, setProgrammeFields] = useState([]);
  const [groups, setGroups] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [showLoginButton, setShowLoginButton] = useState(true);
  const [InternationalCities, InternationalsetCities] = useState([]);

  const fetchUniversitiesByProgrammeAndCity = useCallback(
    async (programmeId, cityId) => {
      if (!programmeId || !cityId) {
        setFilteredUniversities([]);
        return;
      }

      try {
        setIsLoadingUniversities(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/universities-by-programme-and-city/${programmeId}/${cityId}`
        );

        if (!res.ok) throw new Error("Failed to fetch universities");

        const data = await res.json();
        setFilteredUniversities(data.universities || []);
      } catch (err) {
        console.error(
          "Failed to fetch universities by programme and city:",
          err
        );
        setFilteredUniversities([]);
      } finally {
        setIsLoadingUniversities(false);
      }
    },
    []
  );

  const fetchIntakesByProgramme = useCallback(async (programmeId) => {
    if (!programmeId) {
      setIntakes([]);
      return;
    }

    try {
      setIsLoadingIntakes(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-programme-intakes/${programmeId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch intakes");

      const data = await res.json();
      const intakeData = Array.isArray(data) ? data : data?.intakes || [];
      setIntakes(intakeData);
    } catch (err) {
      console.error("Failed to fetch intakes by programme:", err);
      setIntakes([]);
    } finally {
      setIsLoadingIntakes(false);
    }
  }, []);

  const fetchCitiesByCountry = useCallback(async (countryId) => {
    if (!countryId) {
      setCities([]);
      return;
    }

    try {
      setIsLoadingCities(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/c_cities/${countryId}`
      );

      if (!res.ok) throw new Error("Failed to fetch cities");

      const data = await res.json();
      InternationalsetCities(data?.cities || []);
    } catch (err) {
      console.error("Failed to fetch cities by country:", err);
      InternationalsetCities([]);
    } finally {
      setIsLoadingCities(false);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const storedData = localStorage.getItem("register_step1");

        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);
            if (parsedData && typeof parsedData === "object") {
              setFormData((prev) => ({
                ...prev,
                ...parsedData,
              }));
            }
          } catch (error) {
            console.error("Error parsing register_step1:", error);
          }
        }

        const [formOptionsRes, programmeGroupsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/form-options`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programme_groups`),
        ]);

        const formOptions = await formOptionsRes.json();
        const programmeGroupsData = await programmeGroupsRes.json();

        setResidencyOptions(formOptions.residencyStatuses || []);
        setCities(formOptions.cities || []);
        setCountries(formOptions.countries || []);
        setProgrammeGroups(programmeGroupsData || []);
      } catch (err) {
        console.error("Error loading form options", err);
      } finally {
        setIsLoading(false);
        const saved = localStorage.getItem(LOCAL_KEY);
        if (saved) {
          setFormData(JSON.parse(saved));
        }
      }
    };

    loadInitialData();
  }, []);

  const fetchDeliveryPatternsByProgramme = useCallback(async (programmeId) => {
    if (!programmeId) {
      setDeliveryPatterns([]);
      return;
    }

    try {
      let pg = programmeId;

      if (!pg) {
        const storedData = localStorage.getItem("register_step1");
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          if (parsedData.programme) {
            pg = parsedData.programme;
          }
          if (Array.isArray(parsedData) && parsedData[0]?.programme) {
            pg = parsedData[0].programme;
          }
        }
      }

      if (!pg) {
        setDeliveryPatterns([]);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delivery-patterns/${pg}`
      );

      if (!res.ok) throw new Error("Failed to fetch delivery patterns");

      const data = await res.json();
      setDeliveryPatterns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch delivery patterns:", err);
      setDeliveryPatterns([]);
    }
  }, []);

  const fetchProgrammesByGroup = useCallback(async (groupId) => {
    if (!groupId) {
      setProgrammes([]);
      setProgrammeFields([]);
      return;
    }

    try {
      let city = null;
      let pg = null;
      let residency = null;

      const storedData = localStorage.getItem("register_step1");

      if (storedData) {
        const parsedData = JSON.parse(storedData);

        if (parsedData.programmeGroup) {
          pg = parsedData.programmeGroup;
          city = parsedData.city;
          residency = parsedData.residency;
        }

        if (Array.isArray(parsedData) && parsedData[0]?.programmeGroup) {
          pg = parsedData[0].programmeGroup;
          city = parsedData[0].city;
          residency = parsedData[0].residency;
        }
      }

      if (!city || !pg || !residency) {
        console.warn(
          "Missing city, programmeGroup, or residency in stored data"
        );
        setProgrammes([]);
        setProgrammeFields([]);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/programmes-by-groupp/${groupId}/${pg}/${city}/${residency}`
      );

      if (!res.ok)
        throw new Error(`Failed to fetch programmes: ${res.statusText}`);

      const data = await res.json();

      if (Array.isArray(data.programmes) && data.programmes.length > 0) {
        setProgrammes(data.programmes);

        const fields = data.programmes.map((p) => ({
          value: p.programme_id.toString(),
          label: p.programme,
        }));

        setProgrammeFields(fields);
      } else {
        setProgrammes([]);
        setProgrammeFields([
          { value: "", label: "No programmes found for this group" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching programmes by group:", error);
      setProgrammes([]);
      setProgrammeFields([
        { value: "", label: "No programmes found for this group" },
      ]);
    }
  }, []);

  useEffect(() => {
    if (formData.programmeField) {
      fetchDeliveryPatternsByProgramme(formData.programmeField);

      const savedData = localStorage.getItem(LOCAL_KEY);
      let shouldResetDeliveryPattern = true;

      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (
            parsedData.deliveryPattern &&
            parsedData.programmeField === formData.programmeField
          ) {
            shouldResetDeliveryPattern = false;
          }
        } catch (error) {
          console.error("Error parsing saved data:", error);
        }
      }

      if (shouldResetDeliveryPattern) {
        setFormData((prev) => ({
          ...prev,
          deliveryPattern: "",
        }));
      }
    }
  }, [formData.programmeField, fetchDeliveryPatternsByProgramme]);

  useEffect(() => {
    if (formData.groupId && formData.group && formData.programmeGroup) {
      fetchProgrammesByGroup(formData.groupId);
    }
  }, [formData.groupId, fetchProgrammesByGroup]);

  useEffect(() => {
    if (formData.programme && formData.city) {
      fetchUniversitiesByProgrammeAndCity(formData.programme, formData.city);
    } else {
      setFilteredUniversities([]);
    }

    if (formData.programme) {
      fetchIntakesByProgramme(formData.programme);
    } else {
      setIntakes([]);
    }
  }, [
    formData.programme,
    formData.city,
    fetchUniversitiesByProgrammeAndCity,
    fetchIntakesByProgramme,
  ]);

  useEffect(() => {
    const isInternationalStudent =
      formData.residency === "30" || formData.residency === "31";

    if (isInternationalStudent && formData.country) {
      fetchCitiesByCountry(formData.country);
    } else if (!isInternationalStudent && formData.residency) {
      const loadAllCities = async () => {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/form-options`
          );
          const formOptions = await res.json();
          setCities(formOptions.cities || []);
        } catch (err) {
          console.error("Error loading cities:", err);
        }
      };
      loadAllCities();
    }
  }, [formData.country, formData.residency, fetchCitiesByCountry]);

  useEffect(() => {
    const fetchGroupsByProgrammeGroup = async () => {
      if (!formData.programmeGroup) {
        setGroups([]);
        setFormData((prev) => ({
          ...prev,
          group: "",
          groupId: "",
        }));
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/groups-by-programme-group/${formData.programmeGroup}`
        );
        if (!res.ok) throw new Error("Failed to fetch groups");
        const data = await res.json();
        setGroups(Array.isArray(data.groups) ? data.groups : []);
      } catch (err) {
        console.error("Error fetching groups by programme group:", err);
        setGroups([]);
        setFormData((prev) => ({
          ...prev,
          group: "",
          groupId: "",
        }));
      }
    };

    fetchGroupsByProgrammeGroup();
  }, [formData.programmeGroup]);

  useEffect(() => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    try {
      const parsedUser = JSON.parse(userData);
      if (
        parsedUser &&
        typeof parsedUser === "object" &&
        Object.keys(parsedUser).length > 0
      ) {
        setShowLoginButton(false);
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }, []);

  useEffect(() => {
    const selectedProgramme = programmes.find(
      (p) => p.programme_id?.toString() === formData.programme
    );
    if (selectedProgramme) {
      setFormData((prev) => ({
        ...prev,
        message: selectedProgramme.message || "",
        courseFee: selectedProgramme.course_fee || "",
        firstYearFee: selectedProgramme.first_year_fee || "",
        entryLevel: selectedProgramme.entry_level || "",
      }));
    }
  }, [formData.programme, programmes]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "group") {
      const selectedGroup = groups.find((g) => g.id.toString() === value);
      setFormData((prev) => ({
        ...prev,
        group: value,
        groupId: selectedGroup ? selectedGroup.id.toString() : "",
        programmeField: "",
        university: "",
        programme: "",
        intake: "",
        courseFee: "",
        firstYearFee: "",
        entryLevel: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "residency") {
      setFormData((prev) => ({
        ...prev,
        city: "",
        country: "",
        university: "",
        programme: "",
        intake: "",
        courseFee: "",
        firstYearFee: "",
        entryLevel: "",
        euSettlementCode: ["22", "23"].includes(value)
          ? prev.euSettlementCode
          : "",
        programmeGroup: "",
        programmeField: "",
        group: "",
        groupId: "",
      }));
    } else if (name === "country") {
      setFormData((prev) => ({
        ...prev,
        city: "",
        university: "",
      }));
    } else if (name === "programmeGroup") {
      setFormData((prev) => ({
        ...prev,
        programmeField: "",
        university: "",
        programme: "",
        intake: "",
        courseFee: "",
        firstYearFee: "",
        entryLevel: "",
        group: "",
        groupId: "",
      }));
    } else if (name === "programmeField") {
      setFormData((prev) => ({
        ...prev,
        university: "",
        programme: value,
        intake: "",
        courseFee: "",
        firstYearFee: "",
        entryLevel: "",
      }));
    } else if (name === "city") {
      setFormData((prev) => ({
        ...prev,
        university: "",
      }));
    } else if (name === "university") {
      setFormData((prev) => ({
        ...prev,
        intake: "",
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !formData.residency ||
      (["22", "23"].includes(formData.residency) &&
        !formData.euSettlementCode) ||
      !formData.programmeGroup ||
      !formData.groupId ||
      !formData.programmeField ||
      !formData.university ||
      !formData.intake ||
      !formData.deliveryPattern ||
      !formData.loanFunded
    ) {
      alert("Please fill in all required fields");
      return;
    }

    onNext(formData);
  };

  const showSettlementCode = ["28", "29"].includes(formData.residency);
  const isInternationalStudent =
    formData.residency === "30" || formData.residency === "31";

  const showCityField =
    formData.residency === "1" ||
    "27" ||
    formData.residency === "29" ||
    formData.residency === "28";

  const getCityOptions = () => {
    if (isInternationalStudent) {
      return InternationalCities.map((c) => ({
        value: c.id.toString(),
        label: c.city_name,
      }));
    } else {
      return cities.map((c) => ({
        value: c.id.toString(),
        label: c.city_name,
      }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-4 px-3 sm:px-4 lg:px-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-6xl"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden w-full mx-auto"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-center sm:text-left">
                <h1 className="text-xl sm:text-2xl font-bold text-white">
                  Course & University Selection
                </h1>
              </div>

              {!localStorage.getItem("token") && (
                <button
                  onClick={() => {
                    localStorage.clear();
                    navigate("/");
                  }}
                  className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-white text-blue-700 font-semibold hover:bg-blue-50 hover:shadow transition-all duration-300 text-xs whitespace-nowrap self-center sm:self-auto"
                >
                  <span>Already registered?</span>
                  <span className="font-bold">Login</span>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto max-h-[75vh] p-4 sm:p-6">
            {isLoading ? (
              <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <SectionHeader
                    icon={<FaGlobe />}
                    title="Residency & Location"
                    description="Tell us where you're located"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      type="select"
                      label="Residency Status *"
                      name="residency"
                      value={formData.residency}
                      onChange={handleChange}
                      options={residencyOptions.map((r) => ({
                        value: r.id.toString(),
                        label: r.residency,
                      }))}
                      icon={<FaGlobe />}
                    />

                    {showSettlementCode && (
                      <FormField
                        type="text"
                        label="EU Settlement Code *"
                        name="euSettlementCode"
                        value={formData.euSettlementCode}
                        onChange={handleChange}
                        placeholder="Enter your EU settlement code"
                        icon={<FaInfoCircle />}
                        required
                      />
                    )}

                    {isInternationalStudent && (
                      <>
                        <FormField
                          type="select"
                          label="Country *"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          options={countries.map((c) => ({
                            value: c.id.toString(),
                            label: c.nicename,
                          }))}
                          icon={<FaGlobe />}
                        />
                        <FormField
                          type="select"
                          label="City *"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          options={getCityOptions()}
                          icon={<FaMapMarkedAlt />}
                          disabled={!formData.country || isLoadingCities}
                        />
                      </>
                    )}

                    {showCityField && !isInternationalStudent && (
                      <FormField
                        type="select"
                        label="City *"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        options={getCityOptions()}
                        icon={<FaMapMarkedAlt />}
                      />
                    )}
                  </div>
                </motion.div>

                <motion.div
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <SectionHeader
                    icon={<FaGraduationCap />}
                    title="Programme Details"
                    description="Select your course of study"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      type="select"
                      label="Level of Study *"
                      name="programmeGroup"
                      value={formData.programmeGroup}
                      onChange={handleChange}
                      options={programmeGroups.map((p) => ({
                        value: p.id.toString(),
                        label: p.name,
                      }))}
                      icon={<FaGraduationCap />}
                    />

                    <FormField
                      type="select"
                      label="Field Group *"
                      name="group"
                      value={formData.group}
                      onChange={handleChange}
                      options={groups.map((g) => ({
                        value: g.id.toString(),
                        label: g.group_name,
                      }))}
                      icon={<FaGraduationCap />}
                      disabled={!formData.programmeGroup}
                    />

                    <FormField
                      type="select"
                      label="Programme *"
                      name="programmeField"
                      value={formData.programmeField}
                      onChange={handleChange}
                      options={programmeFields}
                      icon={<FaGraduationCap />}
                      disabled={!formData.groupId}
                    />

                    <FormField
                      type="select"
                      label="Delivery Pattern *"
                      name="deliveryPattern"
                      value={formData.deliveryPattern || ""}
                      onChange={handleChange}
                      options={
                        deliveryPatterns.length > 0
                          ? deliveryPatterns.map((d) => ({
                              value: d.id.toString(),
                              label: d.pattern,
                            }))
                          : [
                              {
                                value: "",
                                label: "No delivery patterns available",
                              },
                            ]
                      }
                      icon={<FaInfoCircle />}
                    />
                  </div>
                </motion.div>

                <motion.div
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <SectionHeader
                    icon={<FaUniversity />}
                    title="University & Intake"
                    description="Select where and when you'll study"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      type="select"
                      label="University *"
                      name="university"
                      value={formData.university}
                      onChange={handleChange}
                      options={
                        filteredUniversities && filteredUniversities.length > 0
                          ? filteredUniversities.map((uni) => ({
                              value: uni.id.toString(),
                              label: uni.university_name,
                            }))
                          : [
                              {
                                value: "",
                                label:
                                  "No university found for this city or programme",
                              },
                            ]
                      }
                      icon={<FaUniversity />}
                      disabled={
                        !formData.programme ||
                        !formData.city ||
                        isLoadingUniversities ||
                        !filteredUniversities ||
                        filteredUniversities.length === 0
                      }
                    />

                    {filteredUniversities &&
                      filteredUniversities.length === 0 && (
                        <p className="text-red-500 text-xs mt-1 col-span-full">
                          No universities available for this city or programme.
                        </p>
                      )}

                    <FormField
                      type="select"
                      label="Intake *"
                      name="intake"
                      value={formData.intake}
                      onChange={handleChange}
                      options={intakes.map((intake) => ({
                        value: intake.id.toString(),
                        label: intake.intake,
                      }))}
                      icon={<FaCalendarAlt />}
                      disabled={!formData.programme || isLoadingIntakes}
                    />

                    <FormField
                      type="text"
                      label="Entry Level"
                      name="entryLevel"
                      value={formData.entryLevel}
                      readOnly
                      placeholder="Auto-filled from programme"
                      icon={<FaInfoCircle />}
                    />
                  </div>
                </motion.div>

                <motion.div
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <SectionHeader
                    icon={<FaInfoCircle />}
                    title="Additional Information"
                    description="Tell us more about your funding"
                  />

                  <div className="space-y-4">
                    <motion.div variants={fieldVariants} className="space-y-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Will your fees be funded by Student Loan Company? *
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {["Yes", "No"].map((option) => (
                          <label
                            key={option}
                            className="flex items-center space-x-2 cursor-pointer"
                          >
                            <div className="relative">
                              <input
                                type="radio"
                                name="loanFunded"
                                value={option}
                                onChange={handleChange}
                                checked={formData.loanFunded === option}
                                className="sr-only"
                                required
                              />
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  formData.loanFunded === option
                                    ? "border-blue-600"
                                    : "border-gray-300"
                                }`}
                              >
                                {formData.loanFunded === option && (
                                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                )}
                              </div>
                            </div>
                            <span className="text-xs text-gray-700">
                              {option}
                            </span>
                          </label>
                        ))}
                      </div>
                    </motion.div>

                    <motion.div
                      variants={fieldVariants}
                      className="flex items-start"
                    >
                      <div className="flex items-center h-4 mt-0.5">
                        <input
                          id="apl"
                          type="checkbox"
                          name="apl"
                          checked={formData.apl}
                          onChange={handleChange}
                          className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      <label
                        htmlFor="apl"
                        className="ml-2 text-xs text-gray-700"
                      >
                        Accreditation of prior learning (APL) relates to
                        learning from the past that can be credited against your
                        desired qualification.
                      </label>
                    </motion.div>

                    <FormField
                      type="select"
                      label="How did you hear about us?"
                      name="heardFrom"
                      value={formData.heardFrom}
                      onChange={handleChange}
                      options={[
                        "Friend/Family",
                        "Facebook",
                        "Instagram",
                        "Twitter",
                        "LinkedIn",
                        "TikTok",
                        "Snapchat",
                        "University Website",
                        "Advertisement",
                        "Career Fair",
                        "Search Engine",
                        "Other",
                      ]}
                      icon={<FaInfoCircle />}
                    />
                  </div>
                </motion.div>

                <div className="flex justify-end pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg font-medium text-white hover:opacity-90 transition-all duration-300 shadow hover:shadow-md text-sm"
                  >
                    <span>Continue to Personal Details</span>
                    <FaChevronRight className="text-xs" />
                  </motion.button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}